import { Kushki } from "@kushki/js";
import { EnvironmentEnum } from "@kushki/js/lib/infrastructure/EnvironmentEnum";
import { MerchantSettingsResponse } from "@kushki/js/lib/types/merchant_settings_response";
import {
  Context as ContextRollbar,
  ContextInterface as ContextRollbarInterface,
  getRollbarFromContext,
} from "@rollbar/react";
import { NavigationComponent } from "components/Navigation/NavigationComponent";
import { OverLay } from "components/OverLay/OverLayComponent";
import { defaultTo, get } from "lodash";
import { parse } from "querystring";
import * as React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import {
  LocalizeContextProps,
  Translate,
  withLocalize,
} from "react-localize-redux";
import { connect } from "react-redux";
// tslint:disable-next-line
import ReactResizeDetector from "react-resize-detector";
import { RouteProps } from "react-router";
import { Dispatch } from "redux";
import * as Rollbar from "rollbar";
import { environment } from "root/environments/environment";
import { KushkiInfoProps, PlatformCode } from "root/shared/KushkiInfoEnum";
import { sendHeight } from "root/shared/Messenger";
import {
  availableLanguages,
  getLayoutTranslations,
  isAvaliableLanguage,
} from "root/shared/MultiLanguage";
import { Actions } from "root/store/Actions";
import { CajitaStore, ICajitaConfig } from "root/store/CajitaStore";
import { KushkiInfo } from "types/kushki_info";

// tslint:disable:no-any max-func-body-length
type ICajitaLayoutStateProps = {
  paymentMethods: string[];
  currency: string;
  isConfigInitialized: boolean;
  showOverlay: boolean;
  isSubscription: boolean;
  country: string;
  [k: string]: any;
};
type ICajitaLayoutProps = ICajitaLayoutStateProps &
  LocalizeContextProps &
  RouteProps;

/**
 * CajitaLayout Statefull Component
 */
class CajitaLayoutComponent extends React.Component<ICajitaLayoutProps> {
  public static contextType: React.Context<
    ContextRollbarInterface
  > = ContextRollbar;
  private rollbar?: Rollbar;

  constructor(props: ICajitaLayoutProps) {
    super(props);
    this.props.initialize({
      languages: availableLanguages,
      translation: getLayoutTranslations(),
      options: { renderToStaticMarkup },
    });
    if (this.props.location !== undefined) {
      const params: { [key: string]: string } = parse(
        this.props.location.search.slice(1)
      ) as { [key: string]: string };

      if (params.language !== undefined)
        this.props.setActiveLanguage(
          isAvaliableLanguage(params.language) ? params.language : "es"
        );
      const amount: string = validateAmount(params.amount);

      if (isConfigValid(params) || amount !== "") {
        const kushkiJS: Kushki = new Kushki({
          merchantId: params.merchant_id,
          inTestEnvironment:
            environment.env.toLowerCase() === "test" ||
            environment.env.toLowerCase() === "dev",
          regional: params.regional === "true",
          kushkiInfo: validKushkiInfo(defaultTo(params.kushkiInfo, "")),
        });
        const isTest: boolean =
          environment.env.toLowerCase() === "test" ||
          environment.env.toLowerCase() === "dev";
        const regional: boolean = params.regional === "true";
        const url: string = _getUrl(isTest, regional);

        fetch(`${url}merchant/v1/merchant/settings`, {
          method: "GET",
          mode: "cors",
          headers: {
            "public-merchant-id": params.merchant_id,
          },
        })
          .then(async (response: Response) => response.json())
          .then(async (data: MerchantSettingsResponse) => {
            this.props.setConfig({
              ...params,
              kushkiJS,
              amount: amount,
              is_subscription: params.is_subscription === "true",
              payment_methods:
                params.payment_methods !== undefined
                  ? params.payment_methods.split(",")
                  : ["card"],
              country: data.country,
            });

            this.rollbar = getRollbarFromContext(this.context);

            this.rollbar.configure({
              payload: {
                person: {
                  id: params.merchant_id,
                  username: data.merchant_name,
                },
                custom: params,
              },
            });
          })
          .catch(() =>
            // istanbul ignore next
            this.props.setConfigInitialized({ isInitialized: false })
          );
      }
    }

    return;
  }

  public render(): React.ReactNode {
    let toRender: React.ReactNode;

    if (this.props.isConfigInitialized)
      toRender = (
        <div className={this.props.showOverlay ? "screen-blur" : ""}>
          <NavigationComponent
            components={this.props.paymentMethods}
            currency={this.props.currency}
            isSubscription={this.props.isSubscription}
            country={this.props.country}
          />
        </div>
      );
    else toRender = <Translate id="notAvailable" />;

    return (
      <div id="cajitaLayout" className="box box-white">
        <ReactResizeDetector
          handleHeight={true}
          onResize={(_: number, height: number): void => sendHeight(height)}
        />
        <div className="box-content">
          {this.props.showOverlay ? <OverLay /> : null}
          {this.props.isConfigInitialized === undefined ? null : toRender}
        </div>
      </div>
    );
  }
}

const mapStateToProps: (state: CajitaStore) => object = (
  state: CajitaStore
): object => ({
  paymentMethods: state.config.payment_methods,
  isConfigInitialized: state.config.isInitialized,
  showOverlay: state.showOverlay,
  currency: state.config.currency,
  country: state.config.country,
  isSubscription: state.config.is_subscription,
});

const mapDispatchToProps: (dispatch: Dispatch) => object = (
  dispatch: Dispatch
): object => ({
  setConfig: (payload: ICajitaConfig): object =>
    dispatch({
      type: Actions.SET_CONFIG_PARAMS,
      payload: payload,
    }),
  setConfigInitialized:
    // istanbul ignore next
    (payload: object): object =>
      dispatch({
        type: Actions.SET_CONFIG_INITIALIZED,
        payload: payload,
      }),
});

const CajitaLayout: React.ComponentClass = connect(
  mapStateToProps,
  mapDispatchToProps
)(withLocalize<ICajitaLayoutProps>(CajitaLayoutComponent));

export { CajitaLayout };

function _getUrl(isTest: boolean, regional: boolean): string {
  let url: string = EnvironmentEnum.uat;

  switch (true) {
    case isTest && regional:
      url = EnvironmentEnum.regionalUat;
      break;
    case isTest && !regional:
      url = EnvironmentEnum.uat;
      break;
    case !isTest && regional:
      url = EnvironmentEnum.regionalProd;
      break;
    case !isTest && !regional:
      url = EnvironmentEnum.prod;
      break;
    default:
      break;
  }

  return url;
}

function isConfigValid(obj: object): boolean {
  return (
    "merchant_id" in obj &&
    "amount" in obj &&
    "currency" in obj &&
    "is_subscription" in obj
  );
}

function validateAmount(value: string): string {
  let amount: string = "";

  try {
    amount = JSON.parse(value.replace("$", ""));
  } catch (e) {
    const formattedAmount: RegExpMatchArray | null = value
      .replace("$", "")
      .match(/^[+]?[0-9]{1,9}(?:,?[0-9]{3,})*\.*[0-9]*/g);

    if (formattedAmount !== null)
      amount = JSON.parse(formattedAmount[0].replace(/,/g, ""));
    else amount = "";
  }

  return amount;
}

function isValidJson(kushkiInfo: string): boolean {
  try {
    JSON.parse(kushkiInfo);
  } catch {
    return false;
  }

  return true;
}

function validKushkiInfo(kushkiInfo: string): KushkiInfo {
  if (
    isValidJson(kushkiInfo) &&
    kushkiInfo.includes(KushkiInfoProps.PLATFORM_ID)
  )
    return {
      platformId: get(JSON.parse(kushkiInfo), KushkiInfoProps.PLATFORM_ID),
      platformVersion: get(
        JSON.parse(kushkiInfo),
        KushkiInfoProps.PLATFORM_VERSION,
        ""
      ),
    };

  return { platformId: PlatformCode.CAJITA };
}



// WEBPACK FOOTER //
// ./src/containers/CajitaLayout/CajitaLayoutComponent.tsx